import React, { useState, useRef, useEffect } from "react";
import "../../assets/css/members.css";
import RowContainer from "../../Components/Common/RowContainer";
import Container from "../../Components/Common/Container";
import Label from "../../Components/Common/Label";
import ColumnContainer from "../../Components/Common/ColumnContainer";
import styled from "styled-components";
import CustomDropdown from "../../Components/Common/CustomDropdown";
import LessonOrderRow from "../../Components/LessonOrder/LessonOrderRow";
import formatDate from "../../Components/DateFormat";
import VerticalSpace from "../../Components/VerticalSpace";
import { baseURL, getRequest, postRequest } from "../../api/apiClient";
import "../../stylesheets/indexpage.css";
import Loader from "../../utils/Loading";
import gsap from 'gsap';
import '../../stylesheets/common.css'
import { useResponsiveFlex } from "../../utils/useResponsiveFlex";
import { useSearchParams } from "react-router-dom";

const ScrollableRowContainer = styled(RowContainer)`
  @media (max-width: 1150px) {
    width: 100%;
    overflow-x: auto;
  }
`;

const InnerRowContainer = styled.div`
  display: flex;
  width: 100%;
  min-width: 1100px;
`;

interface LessonItem {
  id: number;
  title: string;
  level: string;
  updatedAt: string;
  active: boolean;
  order: number;
}

const LessonOrder = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [lessons, setLessons] = useState<LessonItem[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<string>(
    searchParams.get("level") || "A1"
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const currentTouchIndex = useRef<number | null>(null);
  const flex = useResponsiveFlex();

  const levelDropdownItems: string[] = ["A1", "A2", "B1", "B2"];

  // Sync state when browser Back/Forward changes the URL
  useEffect(() => {
    const urlLevel = searchParams.get("level");
    if (urlLevel && levelDropdownItems.includes(urlLevel)) {
      setSelectedLevel(urlLevel);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchLessons();
  }, [selectedLevel]);

  const fetchLessons = async () => {
    setLoading(true);
    try {
      const url = `${baseURL}/lesson/get-all-lessons`;
      const body = {
        level: selectedLevel,
      };
      const response = await postRequest(url, body);

      if (response?.data) {
        setLessons(response.data.data.lessons || []);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching lessons:", error);
      setLoading(false);
    }
  };

  const handleLevelSelect = (item: string) => {
    setSelectedLevel(item);
    setSearchParams({ level: item });
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedItemIndex(index);
  };

  const handleDragOver = (
    index: number,
    event: React.DragEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    if (draggedItemIndex !== null && draggedItemIndex !== index) {
      const newLessons = [...lessons];
      const draggedItem = newLessons[draggedItemIndex];
      newLessons.splice(draggedItemIndex, 1);
      newLessons.splice(index, 0, draggedItem);
      setLessons(newLessons);
      setDraggedItemIndex(index);
    }
  };

  const handleDrop = async () => {
    setDraggedItemIndex(null);

    // Save original order before API call
    const originalLessons = [...lessons];

    const updateOrder = lessons.map((item, index) => ({
      id: item.id,
      order: index + 1,
    }));

    try {
      const response = await postRequest(
        `${baseURL}/lesson/change-lesson-order`,
        updateOrder
      );
      if (!response?.data.success) {
        // Restore original order if API fails
        setLessons(originalLessons);
      }
    } catch (error) {
      console.error("Error updating lesson order:", error);
      // Restore original order on error
      setLessons(originalLessons);
    }
  };

  const handleDragEnd = () => {
    setDraggedItemIndex(null);
  };

  const handleTouchStart = (
    index: number,
    event: React.TouchEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    setDraggedItemIndex(index);
    currentTouchIndex.current = index;
    touchStartY.current = event.touches[0].clientY;
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (
      draggedItemIndex === null ||
      touchStartY.current === null ||
      currentTouchIndex.current === null
    ) {
      return;
    }

    const currentY = event.touches[0].clientY;
    const deltaY = currentY - touchStartY.current;

    // Determine direction and swap if necessary
    if (Math.abs(deltaY) > 50) {
      const direction = deltaY > 0 ? 1 : -1;
      const newIndex = currentTouchIndex.current + direction;

      if (newIndex >= 0 && newIndex < lessons.length) {
        const newLessons = [...lessons];
        const draggedItem = newLessons[draggedItemIndex];
        newLessons.splice(draggedItemIndex, 1);
        newLessons.splice(newIndex, 0, draggedItem);
        setLessons(newLessons);
        setDraggedItemIndex(newIndex);
        currentTouchIndex.current = newIndex;
        touchStartY.current = currentY;
      }
    }
  };

  const handleTouchEnd = () => {
    handleDrop();
    touchStartY.current = null;
    currentTouchIndex.current = null;
  };

  useEffect(() => {
    let timeLine = gsap.timeline();
    timeLine.from(".row-container", {
      opacity: 0,
      y: -40,
      duration: 0.3,
      ease: "power2.out",
    });

    timeLine.from(".user-data", {
      opacity: 0,
      y: 40,
      duration: 0.5,
      ease: "power2.out",
      stagger: 0.2
    });
  }, [loading]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="container-fluid">
            <div className="p-3">
              {/* Heading */}
              <div className="row mb-3 ">
                <div className="col">
                  <h2 style={{ fontWeight: "bold" }}>Lesson Order</h2>
                </div>
              </div>

              {/* Filter Label */}
              <div className="row mb-3 ">
                <div className="col">
                  <label htmlFor="filter" className="form-label">
                    Filter
                  </label>
                </div>
              </div>

              {/* Level Filter Only */}
              <div className="row mb-3 all-button-responsive">
                <div className="col d-flex responsive">
                  <CustomDropdown
                    items={levelDropdownItems}
                    onSelect={handleLevelSelect}
                    selectedItem={selectedLevel}
                    height="50px"
                  />
                </div>
              </div>

              <ScrollableRowContainer>
                <InnerRowContainer>
                  <Container width="100%">
                    <RowContainer
                      alignItem="center"
                      padding="8px 12px 8px 12px"
                      gap="8px"
                      className="row-container"
                    >
                      <RowContainer flex="1" alignItem="center">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >

                        </Label>
                      </RowContainer>
                      <RowContainer flex="1" alignItem="center">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Order
                        </Label>
                      </RowContainer>
                      <RowContainer flex={flex.name || "11"}>
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Name
                        </Label>
                      </RowContainer>
                      <RowContainer flex="3">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Level
                        </Label>
                      </RowContainer>
                      <RowContainer flex="5">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Latest updated
                        </Label>
                      </RowContainer>
                      <RowContainer flex="2.5">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Status
                        </Label>
                      </RowContainer>
                    </RowContainer>

                    {lessons && lessons.length > 0 ? (
                      <ColumnContainer>
                        {lessons.map((lesson, index) => (
                          <div
                            key={lesson.id}
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(event) =>
                              handleDragOver(index, event)
                            }
                            onDrop={handleDrop}
                            onDragEnd={handleDragEnd}
                            onTouchStart={(event) =>
                              handleTouchStart(index, event)
                            }
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                            style={{
                              opacity: draggedItemIndex === index ? 0.5 : 1,
                              cursor: "grab",
                              width: "100%",
                            }}
                          >
                            <LessonOrderRow
                              order={index + 1}
                              name={lesson.title}
                              level={lesson.level}
                              created={formatDate(lesson.updatedAt)}
                              status={lesson.active}
                            />
                          </div>
                        ))}
                      </ColumnContainer>
                    ) : (
                      <>
                        <VerticalSpace height={30} />
                        <Label
                          alignItems="center"
                          display="flex"
                          color="rgba(164, 163, 167, 1)"
                          justifyContent="center"
                        >
                          No lessons found for {selectedLevel}.
                        </Label>
                      </>
                    )}
                  </Container>
                </InnerRowContainer>
              </ScrollableRowContainer>

              <VerticalSpace height={100} />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default LessonOrder;
